import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Form, Input, Button, Typography, Flex, Segmented } from "antd";
import loginImg from "../../assets/images/login-img.svg";
import CaptchaWidget from "../../components/Auth/CaptchaWidget";
import { config } from "../../config";

import "../../assets/styles/components_styles/validation.css";
import "../../assets/styles/components_styles/login.css";
import { isValidEmail, isValidPhone } from "../../utils/appUtils";
import { VALIDATION_MESSAGES } from "../../utils/constants";
import Header from "../../components/common/Layouts/Header";
import Footer from "../../components/common/Layouts/Footer";
import HelmetWrapper from "../../components/common/Layouts/HelmetWrapper";
import { verifyUser, selectVerifyUserSuccess, resetVerifyUserSuccess } from "../../redux/userSlice";

const { Title, Text } = Typography;

export default function ForgotPasswordPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const verifyUserSuccess = useSelector(selectVerifyUserSuccess);

    const [errors, setErrors] = useState({});
    const [resetType, setResetType] = useState("email");
    const [formData, setFormData] = useState({ value: "" });
    const [captchaToken, setCaptchaToken] = useState("");
    const captchaRef = useRef(null);
    const requireCaptcha = !!config.turnstileSiteKey;

    // Redirect to update-password page on successful verification
    useEffect(() => {
        if (verifyUserSuccess) {
            dispatch(resetVerifyUserSuccess());
            // Pass the type and value to update-password page
            navigate("/update-password", {
                state: {
                    type: resetType === "phone" ? "mobile" : "email",
                    value: formData.value
                }
            });
        }
    }, [verifyUserSuccess, navigate, dispatch, resetType, formData.value]);

    const handleResetTypeChange = (type) => {
        setResetType(type);
        // Clear value and errors when switching type
        setFormData({ ...formData, value: "" });
        setErrors({});
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        if (errors[e.target.name]) {
            setErrors({
                ...errors,
                [e.target.name]: ""
            });
        }
    };

    const handleSubmit = async () => {
        // Validate based on reset type
        if (!formData.value) {
            const errorMsg = resetType === "email"
                ? VALIDATION_MESSAGES.EMAIL_INVALID
                : VALIDATION_MESSAGES.PHONE_REQUIRED;
            setErrors({ value: errorMsg });
            return;
        }

        if (resetType === "email" && !isValidEmail(formData.value)) {
            setErrors({ value: VALIDATION_MESSAGES.EMAIL_INVALID });
            return;
        }

        if (resetType === "phone" && !isValidPhone(formData.value)) {
            setErrors({ value: VALIDATION_MESSAGES.PHONE_INVALID });
            return;
        }
        if (requireCaptcha && !captchaToken) {
            setErrors({ value: "Please complete the verification." });
            return;
        }

        setErrors({});

        const submissionData = {
            type: resetType === "phone" ? "mobile" : "email",
            value: formData.value
        };
        if (requireCaptcha) submissionData.captchaToken = captchaToken;
        dispatch(verifyUser(submissionData));
    };

    return (
        <div className="loginPageRoot">
            {/* SEO HELMET WRAPPER */}
            <HelmetWrapper
                title="Pinginvite - The Go-To Platform for Local Events in Kolkata & Dubai" 
                description="Pingvite connects people with local events in Kolkata and Dubai through community focus, affordability, 
                and city-specific visibility for organizers and users." 
                keywords="Top Upcoming Events in Kolkata, Best Live Events in Kolkata, best upcoming events in Kolkata, 
                online events near you in Kolkata,  upcoming events in Kolkata, Events in Kolkata, Top Upcoming Events in Dubai, 
                Best Live Events in Dubai, best upcoming events in Dubai, online events near you in Dubai, Event calendar in Dubai, 
                Event tickets in Dubai, upcoming events in Dubai, Events in Dubai"
            /> 

            {/* HEADER */}
            <Header isHome={false} />

            {/* Main content */}
            <main className="loginMain container">
                <div className="loginImage">
                    <img src={loginImg} alt="Forgot Password" className="sideImage" />
                </div>

                {/* right form */}
                <div className="loginFormWrap">
                    <div className="loginCard">
                        <div className="loginCardInner">
                            <div className="loginCardHeader">
                                <Title level={3} className="title">
                                    Verify Account
                                </Title>
                                <Text className="color-neutral">
                                    Enter your email or phone to verify your account.
                                </Text>
                            </div>

                            {/* Reset Type Toggle */}
                            <div className="loginTypeToggle">
                                <Segmented
                                    options={[
                                        {
                                            label: (
                                                <div className="segmentOption">
                                                    <span className="material-symbols-outlined">mail</span>
                                                    <span>Email</span>
                                                </div>
                                            ),
                                            value: "email"
                                        },
                                        {
                                            label: (
                                                <div className="segmentOption">
                                                    <span className="material-symbols-outlined">phone</span>
                                                    <span>Phone</span>
                                                </div>
                                            ),
                                            value: "phone"
                                        }
                                    ]}
                                    value={resetType}
                                    onChange={handleResetTypeChange}
                                    block
                                    size="large"
                                    className="loginSegmented"
                                />
                            </div>

                            <Form name="forgotPassword" layout="vertical" onFinish={handleSubmit}>
                                {/* Dynamic input based on reset type */}
                                <div className="loginInputWrapper">
                                    {resetType === "email" ? (
                                        <Form.Item
                                            required
                                            label="Email ID"
                                            validateStatus={errors.value ? "error" : ""}
                                            help={errors.value}
                                            style={{ marginBottom: 12 }}
                                            key="email-input"
                                        >
                                            <Input
                                                name="value"
                                                value={formData.value}
                                                onChange={handleChange}
                                                prefix={
                                                    <span className="material-symbols-outlined prefix-blue">
                                                        mail
                                                    </span>
                                                }
                                                placeholder="you@example.com"
                                                size="large"
                                                className="hasPrefixIconInput"
                                            />
                                        </Form.Item>
                                    ) : (
                                        <Form.Item
                                            required
                                            label="Phone Number"
                                            validateStatus={errors.value ? "error" : ""}
                                            help={errors.value}
                                            style={{ marginBottom: 12 }}
                                            key="phone-input"
                                        >
                                            <Input
                                                name="value"
                                                value={formData.value}
                                                onChange={handleChange}
                                                prefix={
                                                    <span className="material-symbols-outlined prefix-blue">
                                                        phone
                                                    </span>
                                                }
                                                placeholder="Enter 10-digit phone number"
                                                size="large"
                                                className="hasPrefixIconInput"
                                                maxLength={10}
                                            />
                                        </Form.Item>
                                    )}
                                </div>

                                <CaptchaWidget ref={captchaRef} onToken={setCaptchaToken} />

                                <Form.Item style={{ marginTop: 18 }}>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        block
                                        size="large"
                                        className="btnGradient"
                                    >
                                        Verify
                                    </Button>
                                </Form.Item>
                                <Flex align="center" justify="center">
                                    <Link to="/login">
                                        <Button type="link">Back to Login</Button>
                                    </Link>
                                </Flex>
                            </Form>
                        </div>
                    </div>
                </div>
            </main>

            {/* FOOTER */}
            <Footer />
        </div>
    );
}
